/**
 * Context Info Modal Component
 * Shows all available context information in an organized modal
 */

import React from 'react';
import { X, BookOpen } from 'lucide-react';
import './ContextInfoModal.css';

const ContextInfoModal = ({ isOpen, onClose, scenario, scenarioContext }) => {
  if (!isOpen || !scenario) return null;

  // Map stakeholder IDs to names
  const stakeholderNames = {
    'leadership-ceo': 'Jamie (CEO)',
    'leadership-vp': 'Drew (VP Product)',
    'customer-success': 'Blake (Customer Success)',
    'data-analyst': 'Morgan (Data Analyst)',
    'data-scientist': 'Taylor (Data Scientist)',
    'engineering-backend': 'Alex (Backend Engineer)',
    'engineering-frontend': 'Jordan (Frontend Engineer)',
    'engineering-ml': 'Sam (ML Engineer)',
    'design-ux': 'Riley (UX Designer)',
    'design-ui': 'Casey (UI Designer)'
  };

  // Map channels to display names
  const channelNames = {
    'engineering': 'Engineering',
    'design': 'Design',
    'data': 'Data',
    'leadership': 'Leadership',
    'customer-success': 'Customer Success'
  };

  return (
    <div className="context-info-modal-overlay" onClick={onClose}>
      <div className="context-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="context-info-modal-header">
          <div className="context-info-modal-title">
            <BookOpen size={20} />
            <h2>What You Know</h2>
          </div>
          <button className="context-info-modal-close" onClick={onClose}>
            <X size={20} />
          </button>
        </div>

        <div className="context-info-modal-content">
          {/* Company Info */}
          {scenario.company && (
            <section className="context-info-section">
              <h3>Company Information</h3>
              <div className="context-info-card">
                <div className="info-row">
                  <strong>Company:</strong> {scenario.company.name}
                </div>
                <div className="info-row">
                  <strong>Type:</strong> {scenario.company.type}
                </div>
                {scenario.company.stage && (
                  <div className="info-row">
                    <strong>Stage:</strong> {scenario.company.stage}
                  </div>
                )}
                {scenario.company.product && (
                  <div className="info-row">
                    <strong>Product:</strong> {scenario.company.product}
                  </div>
                )}
                {scenario.company.metrics && (
                  <div className="info-row">
                    <strong>Metrics:</strong>
                    <ul className="info-list">
                      {scenario.company.metrics.users && <li>Active users: {scenario.company.metrics.users}</li>}
                      {scenario.company.metrics.revenue && <li>Revenue: {scenario.company.metrics.revenue}</li>}
                      {scenario.company.metrics.growth && <li>Growth: {scenario.company.metrics.growth}</li>}
                      {scenario.company.metrics.teamSize && <li>Team size: {scenario.company.metrics.teamSize}</li>}
                    </ul>
                  </div>
                )}
                {scenario.company.competitors && (
                  <div className="info-row">
                    <strong>Competitors:</strong> {scenario.company.competitors}
                  </div>
                )}
                {scenario.company.recentEvents && (
                  <div className="info-row">
                    <strong>Recent Events:</strong> {scenario.company.recentEvents}
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Scenario Info */}
          <section className="context-info-section">
            <h3>Scenario Information</h3>
            <div className="context-info-card">
              {scenario.currentSituation && (
                <div className="info-row">
                  <strong>Current Situation:</strong>
                  <p>{scenario.currentSituation}</p>
                </div>
              )}
              {scenario.timeline && (
                <div className="info-row">
                  <strong>Timeline:</strong> {scenario.timeline}
                </div>
              )}
              {scenario.constraints && (
                <div className="info-row">
                  <strong>Constraints:</strong> {scenario.constraints}
                </div>
              )}
            </div>
          </section>

          {/* Current Task */}
          {scenarioContext?.currentTaskTitle && (
            <section className="context-info-section">
              <h3>Current Task</h3>
              <div className="context-info-card">
                <div className="info-row">
                  <strong>Task:</strong> {scenarioContext.currentTaskTitle}
                </div>
                {scenarioContext.currentTaskDescription && (
                  <div className="info-row">
                    <strong>Description:</strong>
                    <p>{scenarioContext.currentTaskDescription}</p>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Stakeholder Knowledge Map */}
          {scenario.stakeholderKnowledge && Object.keys(scenario.stakeholderKnowledge).length > 0 && (
            <section className="context-info-section">
              <h3>Stakeholder Knowledge Map</h3>
              <div className="stakeholder-knowledge-grid">
                {Object.entries(scenario.stakeholderKnowledge).map(([stakeholderId, info]) => {
                  const stakeholderName = stakeholderNames[stakeholderId] || stakeholderId;
                  const channelName = channelNames[info.channel] || info.channel;
                  
                  return (
                    <div key={stakeholderId} className="stakeholder-knowledge-card">
                      <div className="stakeholder-knowledge-header">
                        <strong>{stakeholderName}</strong>
                        <span className="stakeholder-channel-badge">{channelName}</span>
                      </div>
                      <div className="stakeholder-knowledge-content">
                        <p className="stakeholder-knows-label">Knows about:</p>
                        <ul className="stakeholder-knowledge-list">
                          {info.knows.map((topic, idx) => (
                            <li key={idx}>{topic}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContextInfoModal;

